import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useVehicleBalances() {
    const [balances, setBalances] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const { send, isConnected, isOpen, connect, registerHandler } = useWebSocketContext();
    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    const groupId = localStorage.getItem("group_id"); // e.g., "1"
    const roleId = localStorage.getItem("role_id");

    const fetchBalances = useCallback(async (attempt = 0) => {
        if (!mountedRef.current) return;

        setIsLoading(true);
        setError('');
        setMessage('');

        try {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            if (!isConnected) await connect();

            // Wait for connection
            await new Promise((resolve, reject) => {
                const start = Date.now();
                const checkInterval = setInterval(() => {
                    if (!mountedRef.current) {
                        clearInterval(checkInterval);
                        reject(new Error('Component unmounted'));
                    } else if (isOpen()) {
                        clearInterval(checkInterval);
                        resolve();
                    } else if (Date.now() - start > 5000) {
                        clearInterval(checkInterval);
                        reject(new Error('Connection timeout'));
                    }
                }, 100);
            });

            const response = await send('cms_get_vehicle_balances', {
                group_id: groupId,
                role_id: roleId,
                days: "300"
            });

            console.log('Vehicle Balances response:', response);

            if (!response || response.type !== 'cms_get_vehicle_balances_response') {
                throw new Error('Invalid response from server');
            }

            if (!Array.isArray(response.data)) {
                throw new Error('Server did not return a valid vehicle balances list');
            }

            setBalances(response.data);
            setMessage(response.message || '');
        } catch (err) {
            setError(err.message);

            if (mountedRef.current) {
                const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
                retryTimeoutRef.current = setTimeout(() => {
                    fetchBalances(attempt + 1);
                }, delay);
            }
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [isConnected, isOpen, connect, send, groupId, roleId]);

    useEffect(() => {
        mountedRef.current = true;
        fetchBalances();

        const unregister = registerHandler('vehicle_balances_update', (msg) => {
            if (Array.isArray(msg.data) && mountedRef.current) {
                setBalances(msg.data);
            }
        });

        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            unregister();
        };
    }, [fetchBalances, registerHandler]);

    return { balances, isLoading, error, message, refetch: () => fetchBalances(0) };
}
